import { create } from 'zustand';

import { LOCAL_STORAGE_TOKEN_KEY } from '../common/constants';
import AuthenticationService from '../services/AuthenticationServices';
import { ILoginProps } from '../types/login-props-type';
import { Pagination } from '../types/pagination-type';
import { get } from 'lodash';

interface IUserState {
	user: Partial<any> | undefined;
	usersAdmin: any;
	setUser: (newUser: Partial<any>) => void;
	logout: () => void;
	login: (props: ILoginProps) => Promise<void>;
	retrieveUser: () => Promise<void>;
	saveOneSignalId: (oneSignalToken: string) => Promise<void>;
	getAdminUser: () => Promise<any>;
	users: Pagination<any[], 'result'> | undefined;
	getUsers: (
		filter: string,
		currentPage: number,
		itemsForPage: number,
		enabledOnly?: boolean,
	) => Promise<Pagination<any[], 'result'> | undefined>;
	enableUser: (idCliente: string, flag: boolean) => Promise<void>;
	updateCustomer: (idCliente: string, data: any) => Promise<void>;
	createCustomer: (data: any) => Promise<any>;
}

export const useUserStore = create<IUserState>()((set, get) => ({
	user: undefined,
	usersAdmin: [],
	users: undefined,
	getUsers: async (filter, currentPage, itemsForPage, enabledOnly = true) => {
		const response = await AuthenticationService.getUsers(filter, currentPage, itemsForPage, enabledOnly);
		set({ users: response.data });
		return response.data;
	},
	setUser: (newUser) => set({ user: newUser }),
	login: async ({ username, password }) => {
		const response = await AuthenticationService.login({
			username,
			password,
		});
		localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
		set({ user: response.data });
	},
	logout: async () => {
		try {
			if (localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY))
				await AuthenticationService.logout(localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY));
		} catch (error) {
			console.error('Errore durante il logout:', error);
		} finally {
			// Resetta lo stato e rimuovi i dati dal localStorage
			set({ user: undefined });
			localStorage.clear();
		}
	},
	retrieveUser: async () => {
		const jwtToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
		if (!jwtToken) {
			throw new Error('NO_JWT_PROVIDED');
		}
		const response = await AuthenticationService.retrieveCustomer(jwtToken);
		set({ user: response.data });
	},
	saveOneSignalId: async (oneSignalToken: string) => {
		await AuthenticationService.saveOneSignalId(oneSignalToken);
	},
	getAdminUser: async () => {
		const response = await AuthenticationService.getAdminUser();
		set({ usersAdmin: response });
		return response;
	},
	enableUser: async (idCliente, flag) => {
		try {
			// Esegui la chiamata API per abilitare o disabilitare l'utente
			await AuthenticationService.enableUser(idCliente, flag);

			// Aggiorna lo stato dopo aver abilitato/disabilitato l'utente
			set((state) => {
				if (!state.users) return {}; // Evita errori se users è undefined
				console.log('state.users', state.users);
				const updatedUsers = state.users.result
					? state.users.result.map((user) => ({
							...user,
							abilitato: user.id === idCliente ? flag : user.abilitato,
						}))
					: [];

				return {
					users: {
						...state.users, // Mantieni le altre proprietà di `users`
						result: updatedUsers, // Aggiorna solo `result`
					},
				};
			});
		} catch (error) {
			console.error("Errore durante l'abilitazione dell'utente:", error);
		}
	},
	updateCustomer: async (idCliente, data) => {
		await AuthenticationService.updateCustomer(idCliente, data);
		get().getUsers('', 0, 10, true);
	},
	createCustomer: async (data) => {
		const response = await AuthenticationService.createCustomer(data);
		get().getUsers('', 0, 10, true);
		return response;
	},
}));
